import React, { useState } from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity } from 'react-native';
import { Card } from '../ui/Card';
import { DropdownMenu } from '../ui/DropdownMenu';
import { Driver } from '../../types';
import { Ionicons } from '@expo/vector-icons';
import { getRandomDriverImage } from '../../utils/sampleImages';
import theme from '../../theme';

interface DriverGridCardProps {
  driver: Driver;
  onPress?: () => void;
  onViewDetails?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAssign?: () => void;
  onUnassign?: () => void;
}

export const DriverGridCard = ({ driver, onPress, onViewDetails, onEdit, onDelete, onAssign, onUnassign }: DriverGridCardProps) => {
  const [imageError, setImageError] = useState(false);
  
  // Helper to get status color
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return theme.colors.warning; // Active = assigned (orange/warning)
      case 'inactive':
      case 'idle':
        return theme.colors.success; // Inactive = available for assignment (green)
      case 'vacation':
      case 'in vacation':
        return theme.colors.info;
      case 'suspended':
      case 'cancelled':
        return theme.colors.error;
      default:
        return theme.colors.gray400;
    }
  };

  // Helper to get status text
  const getStatusText = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'Assigned'; // Active = assigned to a vehicle
      case 'inactive':
      case 'idle':
        return 'Available'; // Inactive = available for assignment
      case 'vacation':
      case 'in vacation':
        return 'Vacation';
      case 'suspended':
      case 'cancelled':
        return 'Suspended';
      default:
        return 'Unknown';
    }
  };

  const handleMorePress = () => {
    if (onViewDetails) onViewDetails();
  };

  // Define dropdown menu items
  const dropdownItems = [
    {
      id: 'edit',
      label: 'Edit Driver',
      icon: 'pencil',
      onPress: () => onEdit?.(),
    },
    // Show Assign/Unassign based on driver status
    // Active = already assigned (show unassign), Inactive = unassigned (show assign)
    driver.status === 'inactive' ? {
      id: 'assign',
      label: 'Assign Vehicle',
      icon: 'car',
      onPress: () => onAssign?.(),
    } : {
      id: 'unassign',
      label: 'Unassign Vehicle',
      icon: 'remove-circle',
      onPress: () => onUnassign?.(),
    },
    {
      id: 'delete',
      label: 'Delete Driver',
      icon: 'trash',
      color: theme.colors.error,
      onPress: () => onDelete?.(),
    },
  ];

  return (
    <Card
      elevation="medium"
      style={styles.card}
      contentStyle={styles.cardContent}
      onPress={onPress}
    >
      <View style={styles.imageContainer}>
        {imageError || !driver.image_url ? (
          <View style={styles.imagePlaceholder}>
            <Ionicons name="person-outline" size={48} color={theme.colors.gray400} />
          </View>
        ) : (
          <Image
            source={{ uri: driver.image_url }}
            style={styles.driverImage}
            resizeMode="cover"
            onError={() => {
              setImageError(true);
              console.log('Failed to load driver image for:', driver.name);
            }}
          />
        )}
        
        <View style={styles.moreButton}>
          <DropdownMenu 
            items={dropdownItems}
            triggerComponent={
              <View style={styles.moreButtonInner}>
                <Ionicons 
                  name="ellipsis-vertical" 
                  size={16} 
                  color={theme.colors.textSecondary} 
                />
              </View>
            }
          />
        </View>
        
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(driver.status) }]}>
          <Text style={styles.statusText}>{getStatusText(driver.status)}</Text>
        </View>
      </View>
      
      <View style={styles.cardBody}>
        <Text style={styles.name} numberOfLines={1}>
          {driver.name || 'Unknown Driver'}
        </Text>
        
        <Text style={styles.licenseNumber} numberOfLines={1}>
          {driver.licenseNumber || 'No License'}
        </Text>
        
        <View style={styles.details}>
          {driver.phone && (
            <View style={styles.detailRow}>
              <Ionicons name="call-outline" size={12} color={theme.colors.textSecondary} />
              <Text style={styles.detailText} numberOfLines={1}>
                {driver.phone}
              </Text>
            </View>
          )}
          
          {driver.nationality && (
            <View style={styles.detailRow}>
              <Ionicons name="flag-outline" size={12} color={theme.colors.textSecondary} />
              <Text style={styles.detailText} numberOfLines={1}>
                {driver.nationality}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    flex: 1,
    margin: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    minWidth: 180,
    maxWidth: 220,
  },
  cardContent: {
    padding: 0,
  },
  imageContainer: {
    height: 120,
    position: 'relative',
    backgroundColor: theme.colors.gray100,
  },
  driverImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.gray100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreButton: {
    position: 'absolute',
    top: theme.spacing.sm,
    right: theme.spacing.sm,
    zIndex: 10,
  },
  moreButtonInner: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  statusBadge: {
    position: 'absolute',
    top: theme.spacing.sm,
    left: theme.spacing.sm,
    paddingVertical: 3,
    paddingHorizontal: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '500' as any,
    color: theme.colors.white,
  },
  cardBody: {
    padding: theme.spacing.md,
  },
  name: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  licenseNumber: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontFamily: 'monospace',
    marginBottom: theme.spacing.sm,
  },
  details: {
    marginTop: theme.spacing.xs,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  detailText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    marginLeft: theme.spacing.xs,
    flex: 1,
  },
});
